// layerImages.js - image layers keep a LINK to their picture instead of a full copy.
//
// Before, every picture added to a project was stored inside the project as base64 text at its
// original size (one 298x385 layer carried a 2.2 MB PNG). Those copies went with every save and
// auto-save and were loaded every time the project opened.
//
// Now:
//  - pictures already on our server (user uploads) are used by their address;
//  - outside pictures (Pixabay, whose links expire and may not be hotlinked) are copied to our
//    server once (api/Images/SaveLayerImage) and used by that address;
//  - pictures still embedded in older projects are moved to the server on their next save
//    (linkEmbeddedImages), so those projects shrink too.
// Whenever the server cannot be reached, the old way (embedded copy) is kept, so nothing breaks.

import axios from "axios";

const uploaded = new Map(); // data URL fingerprint -> Promise<url>

function authHeader() {
  try {
    const auth = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return auth && auth.access_token ? { Authorization: "Bearer " + auth.access_token } : {};
  } catch {
    return {};
  }
}

function base(apiBaseURL) {
  return String(apiBaseURL || "").replace(/\/$/, "");
}

export function isDataUrl(src) {
  return typeof src === "string" && src.startsWith("data:image/");
}

/** True for addresses on our own API (uploads, stored layer pictures). */
export function isOurPicture(apiBaseURL, src) {
  return typeof src === "string" && !!apiBaseURL && src.toLowerCase().startsWith(base(apiBaseURL).toLowerCase() + "/");
}

function dataUrlToBlob(dataUrl) {
  const comma = dataUrl.indexOf(",");
  const meta = dataUrl.slice(5, comma); // e.g. "image/png;base64"
  const type = meta.split(";")[0] || "image/png";
  const bin = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

function fingerprint(text) {
  // Cheap key for the in-memory cache: length + sampled characters.
  let h = 2166136261;
  const step = Math.max(1, Math.floor(text.length / 4096));
  for (let i = 0; i < text.length; i += step) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36) + ":" + text.length;
}

/** Resolves when the picture at `url` loads in the browser; rejects otherwise (or after 20 s). */
function checkPictureLoads(url) {
  return new Promise((resolve, reject) => {
    if (typeof Image !== "function") return resolve(); // not in a browser (tests)
    const img = new Image();
    const timer = setTimeout(() => reject(new Error("Picture link did not load in time: " + url)), 20000);
    img.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("Picture link does not load: " + url));
    };
    img.src = url;
  });
}

/** Uploads an embedded picture once and resolves to its address on our server. */
export function uploadDataUrl(apiBaseURL, dataUrl) {
  const key = fingerprint(dataUrl);
  let pending = uploaded.get(key);
  if (!pending) {
    pending = (async () => {
      const form = new FormData();
      form.append("file", dataUrlToBlob(dataUrl), "picture");
      const res = await axios.post(base(apiBaseURL) + "/api/Images/SaveLayerImage", form, { headers: authHeader() });
      if (!res.data || !res.data.status || !res.data.path) throw new Error((res.data && res.data.message) || "Picture not saved");
      const url = base(apiBaseURL) + "/" + res.data.path;
      // Only use the link if it really loads. Otherwise the embedded copy is kept, so a server
      // problem can never turn a working picture into a broken one.
      await checkPictureLoads(url);
      return url;
    })();
    pending.catch(() => uploaded.delete(key)); // try again next time
    uploaded.set(key, pending);
  }
  return pending;
}

/** Starts storing the picture on the server without waiting for it (see pictureForLayer). */
function uploadInBackground(apiBaseURL, dataUrl) {
  uploadDataUrl(apiBaseURL, dataUrl).catch((e) =>
    console.log("Could not store the picture on the server yet; it is tried again when the project is saved.", e)
  );
}

/**
 * The address to store in a new image layer.
 * `toDataUrl(url)` is the editor's existing download-as-base64 helper (via our API, so it also
 * works for Pixabay).
 *
 * The picture appears on the canvas right away (as an embedded copy) while it is stored on the
 * server in the background. When the project is saved, linkEmbeddedImages() swaps the copy for
 * the address; by then the upload has normally finished, so the save does not wait either.
 * (Waiting for the upload here made adding a picture take about 3 s instead of about 1 s.)
 */
export async function pictureForLayer(apiBaseURL, url, toDataUrl) {
  if (isDataUrl(url)) {
    uploadInBackground(apiBaseURL, url);
    return url;
  }
  if (isOurPicture(apiBaseURL, url)) return url;
  let dataUrl;
  try {
    dataUrl = await toDataUrl(url);
  } catch (e) {
    return url;
  }
  if (isDataUrl(dataUrl)) uploadInBackground(apiBaseURL, dataUrl);
  return dataUrl;
}

/**
 * Before saving: moves pictures still embedded in image layers to the server and replaces them
 * with their address (in place, so the editor keeps using the address and later saves stay
 * small). Layers whose upload fails keep their embedded copy. `slides` is the editor's slide
 * array (each with LayerData); extra layer arrays (e.g. the active slide's live layers) can be
 * passed too.
 */
export async function linkEmbeddedImages(apiBaseURL, slides, ...extraLayerArrays) {
  const layers = [];
  for (const slide of slides || []) if (slide && Array.isArray(slide.LayerData)) layers.push(...slide.LayerData);
  for (const arr of extraLayerArrays) if (Array.isArray(arr)) layers.push(...arr);
  const todo = layers.filter((l) => l && l.type === "image" && isDataUrl(l.ImageSrc));
  if (!todo.length) return 0;
  let moved = 0;
  await Promise.all(
    todo.map(async (layer) => {
      try {
        const src = layer.ImageSrc;
        const url = await uploadDataUrl(apiBaseURL, src);
        if (layer.ImageSrc === src) {
          layer.ImageSrc = url;
          moved++;
        }
      } catch (e) {
        console.log("Picture stays embedded (could not store it on the server).", e);
      }
    })
  );
  return moved;
}
